import { apiKeyManager } from '@empiriecom/mybuy-session/ApiKey';
import type { RequestPolicy } from 'urql';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { CheckoutOrderableBasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/CheckoutOrderableBasketFragmentFragmentDoc';
import { GetPreparedBasketDocument } from '@packages/gql/generated/shopping/GetPreparedBasketDocument';
import type {
  CheckoutOrderableBasketFragmentFragment,
  GetPreparedBasketQueryVariables,
} from '@packages/gql/generated/shopping/graphql';
import { useAuthQuery } from '@packages/shared/src/hooks/useAuthQuery/useAuthQuery';

/**
 * The unwrapped orderable basket as returned by the GetPreparedBasket query,
 * i.e. the fragment data plus its discriminating `__typename`.
 */
export type OrderableBasket = {
  __typename: 'CheckoutOrderableBasket';
} & CheckoutOrderableBasketFragmentFragment;

/**
 * A single allowed payment as returned by the GetPreparedBasket query. Derived
 * from the fragment so the type always matches the fields actually selected
 * (use this instead of the broad schema type `CheckoutOrderableBasketAllowedPayments`).
 */
export type OrderableAllowedPayment = NonNullable<
  CheckoutOrderableBasketFragmentFragment['allowedPayments'][number]
>;

/**
 * Phone numbers available for the current order, derived from the fragment so
 * the type always matches the fields actually selected (use this instead of the
 * broad schema type `CheckoutPhoneNumbers`).
 */
export type OrderablePhoneNumbers = NonNullable<
  CheckoutOrderableBasketFragmentFragment['phoneNumbers']
>;

export const GetPreparedBasketQuery = /* GraphQL */ `
  query GetPreparedBasket(
    $locale: String!
    $page: CheckoutTargetPage!
    $coupons: String
    $loyaltyCoupons: String
    $addHints: String
    $fp: String
  ) {
    getPreparedBasket(
      locale: $locale
      page: $page
      coupons: $coupons
      loyaltyCoupons: $loyaltyCoupons
      addHints: $addHints
      fp: $fp
    ) {
      __typename
      ... on CheckoutOrderableBasket {
        ...CheckoutOrderableBasketFragment
      }
      ... on CheckoutServerFailure {
        failureMessage
        failureType
      }
    }
  }
`;

// Must map "no codes" to `undefined`, not `''` - urql's `stringifyVariables` drops
// undefined-valued keys but keeps `''` (as `"coupons":""`), which would fragment the cache
// key between a call with durable codes resolved-empty and a caller that omits the key
// entirely, reintroducing the redundant-fetch problem this merge is meant to fix.
export const csvOrUndefined = (codes: string[]) => codes.join(',') || undefined;

export const usePreparedBasket = (
  variables: GetPreparedBasketQueryVariables,
  pause?: boolean,
  requestPolicy: RequestPolicy = 'cache-first',
) => {
  // Bakes the durable coupon/jö-code store into every caller's variables, so a customer who
  // already redeemed a code (e.g. via an affiliate link, before this page ever renders) gets
  // it reflected on the very first request instead of a second, separately-keyed one - `apiKeyManager`'s
  // getters are synchronous, so this costs nothing extra per render. Explicit caller values
  // still win via `??`, though no current caller passes them.
  const mergedVariables: GetPreparedBasketQueryVariables = {
    ...variables,
    coupons: variables.coupons ?? csvOrUndefined(apiKeyManager.getBonuscodes()),
    loyaltyCoupons: variables.loyaltyCoupons ?? csvOrUndefined(apiKeyManager.getLoyaltyCodes()),
  };

  const [result, reexecute] = useAuthQuery({
    query: GetPreparedBasketDocument,
    variables: mergedVariables,
    requestPolicy,
    pause,
  });

  const raw = result.data?.getPreparedBasket;
  const orderableBasket =
    raw?.__typename === 'CheckoutOrderableBasket'
      ? {
          __typename: 'CheckoutOrderableBasket' as const,
          ...getFragmentData(CheckoutOrderableBasketFragmentFragmentDoc, raw),
        }
      : raw;
  const data = result.data ? { getPreparedBasket: orderableBasket } : result.data;

  return [{ ...result, data }, reexecute] as const;
};
